import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TimePicker } from '@unified-checkout/design-system';

import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import { customLocales } from '@app/dashboard/utils/dateRangePicker';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  CREATE_CAMPAIGN_FIELDS,
  DATE_PLACE_HOLDER,
} from '@app/dashboard/constants/reach/modals/create-campaign';
import type { TCurrentTime } from 'libs/design-system/src/types/time_picker';
import type {
  ICreateCampaignDate,
  ICreateCampaignDateProps,
} from '@app/dashboard/types/innerLayout/reach/modals/create-modal';
import DateRanges from '@app/dashboard/components/inner_layout/DateRanges';

import { campaignDatePresets } from '../../../../../utils/create-campaign';

import styles from './DatePicker.module.scss';

const DatePicker = ({
  fields,
  isCreateLoading,
  containerRef,
  handleFieldChange,
  isModalOpen,
}: ICreateCampaignDateProps) => {
  const { lang } = useLangStore();
  const { t } = useTranslation();
  const [restKey, setRestKey] = useState(0);

  const date = fields[CREATE_CAMPAIGN_FIELDS.DATE] as ICreateCampaignDate;
  const time = fields[CREATE_CAMPAIGN_FIELDS.TIME];

  const { index: rangeIndex } = (date?.value as TAppliedRanges) || {};

  const preDefinedRanges = useMemo(() => campaignDatePresets(t), [t]);
  const defaultRange = useMemo(
    () => preDefinedRanges.find((range) => range.default),
    [preDefinedRanges]
  );

  const onApplyHandler = (range: TAppliedRanges) => {
    handleFieldChange({
      fallBackName: CREATE_CAMPAIGN_FIELDS.DATE,
      fallBackValue: range as any,
    });
  };

  const onClearDateHandler = () => {
    handleFieldChange({
      fallBackName: CREATE_CAMPAIGN_FIELDS.DATE,
      fallBackValue: '',
    });
  };

  const onApplyTimeHandler = (currentTime: TCurrentTime | undefined) => {
    if (!currentTime) return;

    handleFieldChange({
      fallBackName: CREATE_CAMPAIGN_FIELDS.TIME,
      fallBackValue: currentTime as any,
    });
  };

  useEffect(() => {
    if (!isModalOpen) {
      setRestKey((prev) => prev + 1);
    }
  }, [isModalOpen]);

  return (
    <div
      className={styles['reach-create-campaign__body-start-date']}
      data-create-campaign-date-status={
        date?.isValid === false ? 'error' : 'default'
      }
      data-create-campaign-time-status={
        time?.isValid === false ? 'error' : 'default'
      }
    >
      <div className={styles['reach-create-campaign__body-start-date-wrapper']}>
        <DateRanges
          width="100%"
          lang={lang}
          shouldHideBtnText={!rangeIndex}
          shouldHideBtnRangeName={true}
          appliedRanges={date?.value as TAppliedRanges}
          handleRangesChange={onApplyHandler}
          onClear={onClearDateHandler}
          customLocale={customLocales}
          customPreDefinedRanges={preDefinedRanges}
          disabled={isCreateLoading}
          containerRef={containerRef}
          placeholder={
            lang === 'en' ? DATE_PLACE_HOLDER.EN : DATE_PLACE_HOLDER.AR
          }
          shouldSelectOneDate
          minDate={defaultRange?.selectionRange[0]}
          maxDate={defaultRange?.selectionRange[1]}
          datePickerLabel={t(
            'innerLayout.reach.campaigns.createCampaign.labels.startDate'
          )}
          key={restKey}
          verticalPlacement="top"
          shouldApplyDefaultRange={false}
        />
        {!!date?.errorMsg && (
          <span
            className={styles['reach-create-campaign__body-start-date-error']}
          >
            {date?.errorMsg}
          </span>
        )}
      </div>
      <div className={styles['reach-create-campaign__body-start-date-wrapper']}>
        <TimePicker
          name={CREATE_CAMPAIGN_FIELDS.TIME}
          onChangeHandler={onApplyTimeHandler}
          defaultValue={(time?.value as TCurrentTime) || ''}
          key={restKey}
          label={{
            text: t(
              'innerLayout.reach.campaigns.createCampaign.labels.campaignTime'
            ),
          }}
          horizontalPlacement="center"
          placeholder={t(
            'innerLayout.reach.campaigns.createCampaign.placeholders.selectTime'
          )}
          disabled={isCreateLoading}
        />
        {!!time?.errorMsg && (
          <span
            className={styles['reach-create-campaign__body-start-date-error']}
          >
            {time?.errorMsg}
          </span>
        )}
      </div>
    </div>
  );
};

export default memo(DatePicker);
